import WeatherMapSegmentedControl from '../../../../../../components/WeatherMapSegmentedControl/WeatherMapSegmentedControl';
import {
  WeatherMapPlaybackButton,
  WeatherMapPlaybackSpeedControl,
} from '../../../../../../components/WeatherMapPlayback/WeatherMapPlayback';
import { useId } from 'react';
import { Form } from 'react-bootstrap';
import {
  HURRICANES_REPLAY_MARKER_OPTIONS,
  HURRICANES_REPLAY_SPEED_OPTIONS,
} from '../../../../config/hurricanes.config';

// Storm Replay section: play/pause, speed, position display mode, and a
// scrubber over the selected storm's chronological track points. State lives
// in useStormReplay so the map layers animate in lockstep with these controls.
const StormReplayPanel = ({ storm, replay, markerMode, onMarkerModeChange }) => {
  const headingId = useId();
  const timelineId = useId();
  const {
    frameIndex,
    lastFrameIndex,
    isPlaying,
    replaySpeed,
    setReplaySpeed,
    togglePlayback,
    scrubToFrame,
  } = replay;

  const records = storm?.records ?? [];
  const hasReplayableTrack = records.length > 1;
  const activeRecord = records[Math.min(frameIndex, records.length - 1)] ?? null;

  return (
    <section
      aria-labelledby={headingId}
      className="hurricanes-replay-panel d-flex flex-shrink-0 flex-column overflow-hidden rounded-3 border"
    >
      <header className="d-flex flex-wrap align-items-start justify-content-between gap-2 border-bottom px-3 py-2">
        <div className="min-w-0">
          <h2 id={headingId} className="m-0 fs-6 fw-bold">
            Storm Replay
          </h2>
          <div className="small text-secondary">Review how the storm moved over time</div>
        </div>
        {hasReplayableTrack && activeRecord && (
          <span className="hurricanes-replay-timestamp small text-secondary text-nowrap">
            {activeRecord.locale_time}
          </span>
        )}
      </header>

      <div className="hurricanes-replay-content d-flex flex-column gap-3 p-3">
        {!storm && (
          <p className="m-0 small text-secondary">Select a single storm to replay its track.</p>
        )}

        {storm && !hasReplayableTrack && (
          <p className="m-0 small text-secondary">Not enough track data to replay this storm.</p>
        )}

        {storm && hasReplayableTrack && (
          <>
            <div>
              <Form.Label htmlFor={timelineId} className="small text-secondary mb-1 fw-semibold">
                Track timeline
              </Form.Label>
              <Form.Range
                id={timelineId}
                min={0}
                max={lastFrameIndex}
                step={1}
                value={frameIndex}
                onChange={(event) => scrubToFrame(event.target.value)}
                aria-label="Scrub storm track timeline"
                aria-valuetext={activeRecord?.locale_time}
              />
              <div className="d-flex justify-content-between small text-secondary">
                <span>{records[0]?.locale_time}</span>
                <span>{records[records.length - 1]?.locale_time}</span>
              </div>
            </div>

            <div className="d-flex flex-wrap align-items-center gap-3">
              <WeatherMapPlaybackButton
                isPlaying={isPlaying}
                onToggle={togglePlayback}
                playLabel="Play storm replay"
                pauseLabel="Pause storm replay"
                showText
                className="px-3"
              />
              <fieldset className="m-0 min-w-0 border-0 p-0">
                <legend className="visually-hidden">Playback speed</legend>
                <WeatherMapPlaybackSpeedControl
                  speeds={HURRICANES_REPLAY_SPEED_OPTIONS}
                  value={replaySpeed}
                  onChange={setReplaySpeed}
                  ariaLabel="Replay speed"
                  buttonClassName="hurricanes-replay-speed"
                />
              </fieldset>
            </div>

            <fieldset className="m-0 min-w-0 border-0 p-0">
              <legend className="mb-1 small text-secondary fw-semibold">Displayed positions</legend>
              <WeatherMapSegmentedControl
                ariaLabel="Displayed positions"
                options={HURRICANES_REPLAY_MARKER_OPTIONS}
                value={markerMode}
                onChange={onMarkerModeChange}
                buttonClassName="px-2 py-1"
              />
            </fieldset>
          </>
        )}
      </div>
    </section>
  );
};

export default StormReplayPanel;
